import { css } from '@emotion/react';
import * as d3 from 'd3';
import React from 'react';

import { PlayButton } from '../Controls/PlayButton';
import { useWindowSizeInfo } from '$/utils/useWindowSize';
import { WebinarPlayerContext } from '$/components/WebinarPlayer/context';
import { ZoomScaleProviderContext } from './Timeline/ZoomScaleProviderContext';
import { Popup } from '$/components/ui/Popup';

const ScaleSlider: React.FC<{ hideZoom?: boolean; children?: React.ReactNode }> = ({
  hideZoom,
  children,
}) => {
  const { zoom, zoomRef, zoomTransform, rescaleAndTranslate } =
    React.useContext(ZoomScaleProviderContext);
  const { colors, isPlaying, setIsPlaying, isEditingInteractions } =
    React.useContext(WebinarPlayerContext);
  const value = zoomTransform ? zoomTransform.k : zoom!.scaleExtent()[0];
  const { md: isMd } = useWindowSizeInfo();

  const ref = React.useCallback(
    (dom: HTMLInputElement | null) => {
      if (!dom) return;
      const root = d3.select(dom);

      root.property('value', value);
      root.on('input', (event) => {
        const cursor = document.getElementById('timelineCursor');

        const cursorOffset =
          parseFloat(cursor!.getAttribute('x') || '0') +
          parseFloat(cursor!.getAttribute('width') || '0') / 2;

        rescaleAndTranslate &&
          rescaleAndTranslate({
            parentEl: d3.select(zoomRef!.current!),
            newZoomValue: event.target.value,
            cursorOffset,
          });
      });

      zoom!.on('zoom.scaleSlider', (event) => {
        root.property('value', event.transform.k);
      });
    },
    [rescaleAndTranslate, value, zoom, zoomRef]
  );

  const percentage = Math.ceil(
    ((value - 1) / (zoom!.scaleExtent()[1] - zoom!.scaleExtent()[0])) * 100
  ).toFixed(0);

  return (
    <div
      className={`relative flex items-center pt-2e pb-1_5e`}
      css={css`
        min-height: 74px;
      `}
    >
      {!hideZoom && (
        <div
          className='flex items-center'
          css={css`
            z-index: 1;
          `}
        >
          {isMd && <div className='text-gray-2 pl-3'>Zoom:</div>}
          <div
            className='p-3 pr-0'
            css={css`
              input {
                width: 100px;
                background: ${colors.zoomSliderBg};
              }
              @media screen and (-webkit-min-device-pixel-ratio: 0) {
                input[type='range']::-webkit-slider-thumb {
                  width: 16px;
                  height: 16px;
                  -webkit-appearance: none;
                  appearance: none;

                  background: #fff;
                  box-shadow: 1px 1px 4px rgba(0, 0, 0, 0.4);
                  border-radius: 30px;
                }
              }
            `}
          >
            <input
              ref={ref}
              type={'range'}
              className='rounded-lg overflow-visible appearance-none cursor-pointer outline-none focus:outline-none cursor-grab h-2'
              min={zoom!.scaleExtent()[0]}
              max={zoom!.scaleExtent()[1]}
              step={(zoom!.scaleExtent()[1] - zoom!.scaleExtent()[0]) / 100000}
            />
          </div>
          {isMd && <div className='text-gray-2 pl-3 xs:hidden'>{`${percentage}%`}</div>}
          {isEditingInteractions && (
            <Popup
              className=''
              content={
                <span>
                  This is the timeline of the attendee experience. The waiting room starts 15
                  minutes before your presentation and the exit room is a maximum of 30 minutes long
                  after your presentation.
                  <br />
                  <br />
                  Drag the playhead tool to the moment on the timeline where you would like to add
                  an interaction.
                  <br />
                  <br />
                  You may add interactions in the waiting room to engage with attendees early, and
                  add interactions in the exit room to ask for suggestions and feedback on your
                  presentation.
                </span>
              }
              key='Timeline'
            />
          )}
        </div>
      )}

      <div
        className='flex absolute w-full justify-center items-center pointer-events-none'
        css={css`
          left: 0;
          right: 0;
          bottom: 50%;
        `}
      >
        <div
          css={css`
            pointer-events: all;
            color: #fff;
            width: 64px;
            height: 64px;
            left: 0
            transform-origin: center;
            transform: translateY(50%) scale(0.75);

            #playButton {
              color: #fff;
              circle {
                fill: #39a1b2;
                fill-opacity: 1;
                transform-origin: center center;
              }
            }
          `}
        >
          <PlayButton
            onClick={(): void => {
              setIsPlaying(!isPlaying);
            }}
          />
        </div>
      </div>
      {children}
    </div>
  );
};

export default ScaleSlider;
